import { AuthorizationError } from '@/errors/AuthorizationError'
import {
  QueryClient,
  defaultShouldDehydrateQuery,
  isServer,
} from '@tanstack/react-query'
import { cache } from 'react'

function queryDataHasResponse(data: unknown): boolean {
  if (typeof Response !== 'undefined' && data instanceof Response) return true
  if (!data || typeof data !== 'object') return false
  const pages = (data as { pages?: unknown }).pages
  return Array.isArray(pages) && pages.some(page => page instanceof Response)
}

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000 * 5,
        retry(failureCount, error) {
          if (error instanceof AuthorizationError || failureCount >= 5) return false
          return true
        },
        throwOnError(error, data) {
          return true
        }
      },
      dehydrate: {
        // include pending queries in dehydration
        shouldDehydrateQuery: (query) => {
          const should =
            defaultShouldDehydrateQuery(query) ||
            query.state.status === 'pending'
          if (!should) return false
          // Response 无法穿过 Server → Client，带上会让整页 RSC 序列化失败
          return !queryDataHasResponse(query.state.data)
        },
      },
    },
  })
}

let browserQueryClient: QueryClient | undefined = undefined

// Server: one client per request (request-scoped via React cache), so that
// generateMetadata / layout / page / providers share the same client and
// dedupe data fetching within a single request instead of refetching.
const getServerQueryClient = cache(() => makeQueryClient())

export function getQueryClient() {
  if (isServer) {
    return getServerQueryClient()
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient()
    return browserQueryClient
  }
}
